/**
 * AuditLogPage — 149-ck
 *
 * Server-side pagination (25/50/100, default 25). Filter aktor & aksi
 * dilempar ke backend supaya query cursor tidak overload (500 baris lokal
 * di-remove — sekarang cursor hanya ambil sesuai page).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileClock, Filter, RefreshCw, ChevronLeft, ChevronRight, Search } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, formatIDR } from "@/lib/constants";
import StickyContextBar from "@/components/StickyContextBar";
import { Input } from "@/components/ui/input";

const ACTION_LABEL = {
  create: { label: "Buat", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  update: { label: "Ubah", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  close: { label: "Close", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  reopen: { label: "Reopen", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  delete: { label: "Hapus", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

const PROJECT_TYPE_LABEL = {
  new_project: "New",
  variation_order: "VO",
  working_order: "WO",
  internal: "Internal",
};

const PAGE_SIZES = [25, 50, 100];

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtValue(v) {
  if (v == null) return "-";
  if (typeof v === "object") {
    return Object.entries(v)
      .map(([k, val]) => `${k}: ${k === "contract_value" && val != null ? formatIDR(val) : String(val)}`)
      .join(" · ");
  }
  if (typeof v === "number") return v.toLocaleString("id-ID");
  return String(v);
}

export default function AuditLogPage() {
  const { user } = useAuth();
  // 2026-02-13 (user request): akses Audit Log tidak lagi hardcode BOD.
  // Backend memfilter data berdasarkan scope viewer (hierarki level + grants override).
  // Frontend gate: BOD dibolehkan optimistik; user lain menunggu response backend
  // (jika 403 → tampilkan pesan forbidden).
  const [forbidden, setForbidden] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [actionFilter, setActionFilter] = useState("all");
  const [actorFilter, setActorFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { category: "project", page, page_size: pageSize };
      if (actionFilter !== "all") params.action = actionFilter;
      if (actorFilter) params.actor_name = actorFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/audit-logs", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setScope(data.scope || "");
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      setError(e.response?.data?.detail || e.message || "Gagal muat audit log");
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, actionFilter, actorFilter, q]);

  useEffect(() => { load(); }, [load]);

  // Reset ke page 1 saat filter berubah (side-effect via setState di handler).
  const changePageSize = (v) => { setPageSize(v); setPage(1); };
  const changeActionFilter = (v) => { setActionFilter(v); setPage(1); };
  const changeActorFilter = (v) => { setActorFilter(v); setPage(1); };
  const submitSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  if (forbidden) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-800" data-testid="audit-forbidden">
          Anda tidak memiliki akses ke audit log. Minta admin memberi perm "Lihat" pada "Akses Audit Log" di Access Control.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-slate-500">Compliance</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Project Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">
            Rekam jejak siapa membuat, mengubah, menutup, membuka, atau menghapus project. Untuk keperluan compliance.
            {scope && <span className="ml-2 text-[10px] text-slate-500">· Cakupan Anda: {scope}</span>}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="audit-refresh"
          className="h-9 px-3 border border-slate-300 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <StickyContextBar
        segments={["Compliance", "Project Audit Log", `${total} entri`]}
        extra={<span className="text-slate-400 normal-case">Hal {page}/{totalPages} · {pageSize}/hal</span>}
      />

      <form onSubmit={submitSearch} className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 px-3 py-2">
        <Filter size={13} className="text-slate-500" />
        <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Aksi</label>
        <select
          value={actionFilter}
          onChange={(e) => changeActionFilter(e.target.value)}
          data-testid="audit-filter-action"
          className="h-8 px-2 border border-slate-300 text-xs bg-white"
        >
          <option value="all">Semua</option>
          {Object.entries(ACTION_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 ml-2">Aktor</label>
        <Input
          type="text"
          value={actorFilter}
          onChange={(e) => changeActorFilter(e.target.value)}
          data-testid="audit-filter-actor"
          placeholder="Nama aktor…"
          className="h-8 text-xs w-40"
        />
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setPage(1); load(); } }}
            data-testid="audit-search"
            placeholder="Cari (project / field / user)…"
            className="h-8 text-xs pl-6 w-56"
          />
        </div>
        <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 ml-2">Baris/hal</label>
        <select
          value={pageSize}
          onChange={(e) => changePageSize(Number(e.target.value))}
          data-testid="audit-page-size"
          className="h-8 px-2 border border-slate-300 text-xs bg-white"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="ml-auto text-[11px] text-slate-500 tabular-nums" data-testid="audit-count">
          {total} entri · Hal {page}/{totalPages}
        </span>
      </form>

      {error && (
        <div className="bg-rose-50 border border-rose-300 px-4 py-3 text-sm text-rose-800" data-testid="audit-error">
          Gagal memuat audit log: {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="audit-table-wrap">
        <table className="w-full text-xs table-auto">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              {["Waktu", "Aksi", "Aktor", "Divisi/Lv", "Project", "Field", "Nilai Lama → Baru"].map((h) => (
                <th key={h} className="px-3 py-2 overline text-slate-600 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="audit-table">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 italic" data-testid="audit-loading">Memuat…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 italic" data-testid="audit-empty">
                {total === 0 ? "Belum ada entri audit yang cocok filter." : "Halaman ini kosong. Coba halaman sebelumnya."}
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const act = ACTION_LABEL[r.action] || { label: r.action || "?", cls: "bg-slate-100 text-slate-700 border-slate-300" };
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{fmtDateTime(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${act.cls}`}>
                      {act.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-[#0B1D3A]">{r.actor_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-wider text-slate-500">
                    {r.actor_division} · Lv{r.actor_level}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#0B1D3A]">{r.project_name || r.target_user_name || "-"}</div>
                    {r.project_type && (
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                        {PROJECT_TYPE_LABEL[r.project_type] || r.project_type}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                    {r.field === "__create__" ? "—" : r.field === "__delete__" ? "—" : r.field}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {r.action === "create" ? (
                      <span className="text-emerald-700">{fmtValue(r.new_value)}</span>
                    ) : r.action === "delete" ? (
                      <span className="text-rose-700">Dihapus permanen</span>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-slate-500 line-through">{fmtValue(r.old_value)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-semibold text-[#0B1D3A]">{fmtValue(r.new_value)}</span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between text-xs text-slate-600" data-testid="audit-pagination">
        <div className="tabular-nums">
          Menampilkan {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length} dari {total}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="audit-page-prev"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="h-8 px-2 border border-slate-300 hover:bg-slate-50 uppercase tracking-wider font-semibold text-[11px] disabled:opacity-40 flex items-center gap-1"
          ><ChevronLeft size={13} /> Sebelumnya</button>
          <span className="px-2 tabular-nums" data-testid="audit-page-indicator">Hal {page} / {totalPages}</span>
          <button
            type="button"
            data-testid="audit-page-next"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="h-8 px-2 border border-slate-300 hover:bg-slate-50 uppercase tracking-wider font-semibold text-[11px] disabled:opacity-40 flex items-center gap-1"
          >Berikutnya <ChevronRight size={13} /></button>
        </div>
      </div>
    </div>
  );
}
